<template>
  <!--
    เส้นทางครูผู้ช่วย (ส่วนกลาง) — ขั้นปัจจุบัน / ผู้ต้องดำเนินการ / เลยกำหนด ของครูผู้ช่วยทุกคน
    API: assistant_teacher_monitor.php action=pipeline (ตรรกะ assistant_teacher_stage_helpers.php — ชุดเดียวกับศูนย์แจ้งเตือนสถานศึกษา)
  -->
  <v-card outlined rounded="lg" class="mb-6">
    <v-card-title class="subtitle-1 font-weight-bold">
      <v-icon left color="primary">mdi-transit-connection-variant</v-icon>
      เส้นทางครูผู้ช่วย: ค้างที่ขั้นไหน ใครต้องดำเนินการ
      <v-spacer />
      <v-btn small outlined color="deep-purple" class="mr-2" :loading="syncLoading" @click="previewSync">
        <v-icon small left>mdi-sync-alert</v-icon>ตรวจความตรงกันของผลประเมิน
      </v-btn>
      <v-btn small outlined color="success" class="mr-2" :disabled="!rows.length" @click="exportXlsx">
        <v-icon small left>mdi-microsoft-excel</v-icon>Excel
      </v-btn>
      <v-btn small outlined :loading="loading" @click="load"><v-icon small left>mdi-refresh</v-icon>โหลดใหม่</v-btn>
    </v-card-title>
    <v-card-text>
      <v-progress-linear v-if="loading" indeterminate class="mb-2" />
      <template v-if="summary">
        <!-- ขั้นตอน (กดเพื่อกรอง) -->
        <div class="d-flex flex-wrap mb-2" style="gap:6px">
          <div v-for="(s, key) in stages" :key="key" class="stage-box" :class="{ active: stageFilter === key }"
            @click="stageFilter = stageFilter === key ? null : key">
            <div class="caption grey--text">ขั้น {{ s[0] }}</div>
            <div class="body-2 font-weight-medium">{{ s[1] }}</div>
            <div class="stage-count">{{ fmt(summary.by_stage[key]) }}</div>
          </div>
        </div>
        <div class="d-flex flex-wrap align-center mb-3" style="gap:6px">
          <span class="caption mr-1">ผู้ต้องดำเนินการ:</span>
          <v-chip v-for="(label, key) in ownerChips" :key="key" small :outlined="ownerFilter !== key" :color="OWNER_COLOR[key]"
            :dark="ownerFilter === key" @click="ownerFilter = ownerFilter === key ? null : key">
            {{ label }} {{ fmt(summary.by_owner[key]) }}
          </v-chip>
          <v-chip small :outlined="!overdueOnly" color="error" :dark="overdueOnly" @click="overdueOnly = !overdueOnly">
            <v-icon small left>mdi-alarm</v-icon>เลยกำหนด {{ fmt(summary.overdue) }}
            (ส่วนกลาง {{ fmt(summary.overdue_central) }})
          </v-chip>
          <v-chip small :outlined="!collegePendingOnly" color="deep-orange" :dark="collegePendingOnly"
            @click="collegePendingOnly = !collegePendingOnly">
            <v-icon small left>mdi-domain</v-icon>มีงานค้างของสถานศึกษา {{ fmt(summary.college_pending) }}
          </v-chip>
        </div>

        <v-tabs v-model="view" dense class="mb-2">
          <v-tab>รายคน ({{ fmt(filtered.length) }})</v-tab>
          <v-tab>รายสถานศึกษา ({{ fmt(byCollege.length) }})</v-tab>
        </v-tabs>
        <v-tabs-items v-model="view">
          <v-tab-item>
            <v-row dense class="mb-1">
              <v-col cols="12" md="5">
                <v-text-field v-model="search" dense outlined hide-details clearable prepend-inner-icon="mdi-magnify"
                  label="ค้นหาชื่อ/เลขบัตร/สถานศึกษา" />
              </v-col>
              <v-col cols="12" md="4">
                <v-select v-model="flagFilter" :items="flagItems" dense outlined hide-details clearable label="ความผิดปกติของข้อมูล" />
              </v-col>
            </v-row>
            <v-data-table :headers="headers" :items="filtered" :search="search" dense item-key="id_card" :items-per-page="50"
              :footer-props="{ 'items-per-page-options': [50, 100, 500] }" sort-by="overdue_days" sort-desc class="pipe-table">
              <template v-slot:[`item.full_name`]="{ item }">
                <div class="font-weight-medium">{{ item.full_name }}</div>
                <div class="caption grey--text">{{ item.id_card }} · {{ item.college_name || item.college_code }}</div>
              </template>
              <template v-slot:[`item.stage_no`]="{ item }">
                <v-chip x-small :color="item.stage === 'promoted' ? 'success' : 'primary'" dark>{{ item.stage_no }}. {{ item.stage_label }}</v-chip>
              </template>
              <template v-slot:[`item.rounds`]="{ item }">
                <v-tooltip v-for="r in item.rounds" :key="r.times" bottom>
                  <template v-slot:activator="{ on }">
                    <span class="round-dot" :style="{ background: roundColor(r) }" v-on="on">{{ r.times }}</span>
                  </template>
                  <span>ครั้งที่ {{ r.times }}: {{ ROUND_TEXT[r.state] }} · ครบกำหนด {{ thaiDate(r.due_date) }}
                    {{ r.overdue_days ? `(เลย ${r.overdue_days} วัน)` : "" }}</span>
                </v-tooltip>
              </template>
              <template v-slot:[`item.action`]="{ item }">
                <v-chip x-small :color="OWNER_COLOR[item.owner]" dark class="mr-1">{{ item.owner_label }}</v-chip>
                <span class="caption">{{ item.action }}</span>
                <div v-if="item.flags.length" class="caption orange--text text--darken-3">
                  <v-icon x-small color="orange darken-3">mdi-alert</v-icon> {{ item.flags.map(f => flags[f] || f).join(" · ") }}
                </div>
              </template>
              <template v-slot:[`item.overdue_days`]="{ item }">
                <span v-if="item.overdue_days > 0" class="error--text font-weight-bold">{{ fmt(item.overdue_days) }} วัน</span>
                <span v-else class="caption grey--text">{{ item.due_date ? thaiDate(item.due_date) : "-" }}</span>
              </template>
            </v-data-table>
          </v-tab-item>
          <v-tab-item>
            <v-data-table :headers="collegeHeaders" :items="byCollege" dense :items-per-page="50" item-key="college_code"
              :footer-props="{ 'items-per-page-options': [50, 100, -1] }">
              <template v-slot:[`item.college_name`]="{ item }">
                <a @click="pickCollege(item)">{{ item.college_name || item.college_code }}</a>
                <div class="caption grey--text">{{ item.college_code }}</div>
              </template>
              <template v-slot:[`item.stages`]="{ item }">
                <span v-for="(n, key) in item.by_stage" v-show="n" :key="key" class="caption mr-2">
                  {{ stages[key][0] }}.{{ stages[key][1] }} <b>{{ n }}</b>
                </span>
              </template>
              <template v-slot:[`item.overdue_college`]="{ item }">
                <b :class="item.overdue_college ? 'error--text' : 'grey--text'">{{ item.overdue_college }}</b>
              </template>
            </v-data-table>
          </v-tab-item>
        </v-tabs-items>
      </template>
    </v-card-text>

    <!-- ผลประเมิน: แหล่งจริง (assistant_teacher) ↔ สำเนาในบัญชีครูผู้ช่วย (mt_time_N_*) -->
    <v-dialog v-model="syncDialog" max-width="1100" scrollable>
      <v-card v-if="sync">
        <v-toolbar dense dark flat color="deep-purple" class="flex-grow-0">
          <v-toolbar-title class="subtitle-1">ความตรงกันของผลประเมินครูผู้ช่วย</v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="syncDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-3">
          <v-alert :type="sync.applied ? 'success' : sync.summary.total ? 'warning' : 'success'" dense text class="caption">
            <template v-if="sync.applied">ปรับสำเนาให้ตรงกับผลประเมินจริงแล้ว {{ fmt(sync.summary.total) }} รายการ</template>
            <template v-else-if="!sync.summary.total">ผลประเมินในบัญชีครูผู้ช่วยตรงกับข้อมูลจริงทั้งหมด</template>
            <template v-else>
              ไม่ตรงกัน {{ fmt(sync.summary.total) }} รายการ ({{ fmt(sync.summary.people) }} คน) —
              <b>สำเนาแสดง “ผ่าน” แต่ผลจริงไม่ผ่าน/ยังไม่ตรวจ/ถูกลบ {{ fmt(sync.summary.false_pass) }} รายการ</b>
              ซึ่งเงื่อนไขอนุมัติ คศ.1 จะนับเป็นผ่าน · แหล่งจริงคือผลประเมินที่สถานศึกษาส่งและส่วนกลางตรวจ
              (ข้อมูลนำเข้าจากระบบเดิมที่ไม่มีแถวประเมินจะไม่ถูกแก้)
            </template>
          </v-alert>
          <v-data-table :headers="syncHeaders" :items="sync.changes" dense :items-per-page="50" item-key="key"
            :footer-props="{ 'items-per-page-options': [50, 100, -1] }" no-data-text="ไม่มีรายการ">
            <template v-slot:[`item.full_name`]="{ item }">
              {{ item.full_name }}<div class="caption grey--text">{{ item.id_card }} · {{ item.college_name }}</div>
            </template>
            <template v-slot:[`item.reason`]="{ item }">
              <v-chip x-small :color="item.reason === 'deleted' ? 'error' : 'indigo'" dark>
                {{ item.reason === "deleted" ? "แถวประเมินถูกลบแล้ว" : "คัดลอกจากผลจริง" }}
              </v-chip>
            </template>
            <template v-slot:[`item.ck`]="{ item }">
              <b>{{ item.before.ck || "(ว่าง)" }}</b> → <b :class="item.after.ck === 'Pass' ? 'success--text' : 'error--text'">{{ item.after.ck || "(ว่าง)" }}</b>
            </template>
            <template v-slot:[`item.score`]="{ item }">{{ item.before.score || "-" }} → {{ item.after.score || "-" }}</template>
          </v-data-table>
        </v-card-text>
        <v-card-actions v-if="!sync.applied && sync.summary.total">
          <v-spacer />
          <v-btn text @click="syncDialog = false">ปิด</v-btn>
          <v-btn color="deep-purple" dark :loading="syncLoading" @click="applySync">ปรับให้ตรงกับผลจริง {{ fmt(sync.summary.total) }} รายการ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import * as XLSX from "xlsx";

const OWNER_COLOR = { college: "deep-orange", central: "indigo", person: "teal", wait: "blue-grey", none: "grey" };
const ROUND_TEXT = { pass: "ผ่าน", fail: "ไม่ผ่าน", pending: "ส่งแล้ว รอส่วนกลางตรวจ", missing: "ยังไม่ส่งผล" };

export default {
  name: "AssistantTeacherPipeline",
  data() {
    return {
      OWNER_COLOR,
      ROUND_TEXT,
      loading: false,
      summary: null,
      stages: {},
      owners: {},
      flags: {},
      rows: [],
      byCollege: [],
      view: 0,
      search: "",
      stageFilter: null,
      ownerFilter: null,
      flagFilter: null,
      overdueOnly: false,
      collegePendingOnly: false,
      sync: null,
      syncDialog: false,
      syncLoading: false,
      syncHeaders: [
        { text: "ครูผู้ช่วย", value: "full_name" },
        { text: "ครั้งที่", value: "times", align: "center", width: 70 },
        { text: "สาเหตุ", value: "reason", width: 170 },
        { text: "ผลตรวจ (สำเนา → จริง)", value: "ck", sortable: false, width: 190 },
        { text: "คะแนน", value: "score", sortable: false, width: 140 }
      ],
      headers: [
        { text: "ครูผู้ช่วย", value: "full_name" },
        { text: "ขั้นปัจจุบัน", value: "stage_no", width: 190 },
        { text: "ประเมิน 1–4", value: "rounds", sortable: false, width: 130 },
        { text: "ผู้ต้องดำเนินการ / สิ่งที่ต้องทำ", value: "action", sortable: false },
        { text: "เลยกำหนด", value: "overdue_days", width: 110 }
      ],
      collegeHeaders: [
        { text: "สถานศึกษา", value: "college_name" },
        { text: "ครูผู้ช่วย", value: "total", align: "center", width: 90 },
        { text: "งานค้างของสถานศึกษา", value: "overdue_college", align: "center", width: 150 },
        { text: "เลยกำหนด (ส่วนกลาง)", value: "overdue_central", align: "center", width: 150 },
        { text: "จำนวนตามขั้น", value: "stages", sortable: false }
      ]
    };
  },
  computed: {
    ownerChips() {
      const o = { ...this.owners };
      delete o.none;
      return o;
    },
    flagItems() {
      return Object.entries(this.flags).map(([value, text]) => ({ value, text }));
    },
    filtered() {
      return this.rows.filter(
        p =>
          (!this.stageFilter || p.stage === this.stageFilter) &&
          (!this.ownerFilter || p.owner === this.ownerFilter) &&
          (!this.flagFilter || p.flags.includes(this.flagFilter)) &&
          (!this.overdueOnly || p.overdue_days > 0) &&
          (!this.collegePendingOnly || p.college_pending)
      );
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    fmt(v) {
      return Number(v || 0).toLocaleString("th-TH");
    },
    thaiDate(ymd) {
      if (!ymd) return "-";
      const m = ["", "ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
      const [y, mo, d] = ymd.split("-").map(Number);
      return `${d} ${m[mo]} ${y + 543}`;
    },
    // สีคงที่ (ธีมของระบบทับสีชื่อของ Vuetify) — ผ่าน เขียว / รอส่วนกลางตรวจ น้ำเงิน / ไม่ผ่าน แดง / เลยกำหนด ส้ม / ยังไม่ถึงกำหนด เทา
    roundColor(r) {
      if (r.state === "pass") return "#2e7d32";
      if (r.state === "fail") return "#c62828";
      if (r.state === "pending") return "#3949ab";
      return r.overdue_days > 0 ? "#ef6c00" : "#bdbdbd";
    },
    async load() {
      this.loading = true;
      try {
        const res = await this.$http.post("assistant_teacher_monitor.php", { ApiKey: "HRvec2021", action: "pipeline" });
        if (!res.data || res.data.status !== true) throw new Error((res.data && res.data.message) || "โหลดข้อมูลไม่สำเร็จ");
        Object.assign(this, {
          summary: res.data.summary,
          stages: res.data.stages,
          owners: res.data.owners,
          flags: res.data.flags,
          rows: res.data.rows,
          byCollege: res.data.by_college
        });
      } catch (e) {
        this.$emit("error", e.message);
      } finally {
        this.loading = false;
      }
    },
    async callSync(apply) {
      this.syncLoading = true;
      try {
        const res = await this.$http.post("assistant_teacher_monitor.php", { ApiKey: "HRvec2021", action: "score_sync", apply });
        if (!res.data || res.data.status !== true) throw new Error((res.data && res.data.message) || "ตรวจไม่สำเร็จ");
        res.data.changes.forEach(c => (c.key = `${c.mt_id}-${c.times}`));
        this.sync = res.data;
        this.syncDialog = true;
        if (apply) this.load();
      } catch (e) {
        this.$emit("error", e.message);
      } finally {
        this.syncLoading = false;
      }
    },
    previewSync() {
      this.callSync(false);
    },
    applySync() {
      if (!confirm(`ปรับผลประเมินในบัญชีครูผู้ช่วย ${this.sync.summary.total} รายการให้ตรงกับผลประเมินจริง?`)) return;
      this.callSync(true);
    },
    pickCollege(c) {
      this.search = c.college_code;
      this.view = 0;
    },
    exportXlsx() {
      const rows = this.filtered.map((p, i) => ({
        ลำดับ: i + 1,
        เลขบัตรประชาชน: p.id_card,
        "ชื่อ-สกุล": p.full_name,
        รหัสสถานศึกษา: p.college_code,
        สถานศึกษา: p.college_name,
        วันบรรจุ: this.thaiDate(p.appoint_date),
        ขั้นปัจจุบัน: `${p.stage_no}. ${p.stage_label}`,
        ผู้ต้องดำเนินการ: p.owner_label,
        สิ่งที่ต้องทำ: p.action,
        ครบกำหนด: this.thaiDate(p.due_date),
        "เลยกำหนด (วัน)": p.overdue_days || "",
        ...Object.fromEntries(p.rounds.map(r => [`ประเมินครั้งที่ ${r.times}`, ROUND_TEXT[r.state]])),
        ความผิดปกติของข้อมูล: p.flags.map(f => this.flags[f] || f).join(" · ")
      }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "เส้นทางครูผู้ช่วย");
      XLSX.writeFile(wb, "เส้นทางครูผู้ช่วย.xlsx");
    }
  }
};
</script>

<style scoped>
.stage-box {
  flex: 1 1 110px;
  padding: 8px 10px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  cursor: pointer;
}
.stage-box.active {
  border-color: #1976d2;
  background: rgba(25, 118, 210, 0.08);
}
.stage-count {
  font-size: 22px;
  font-weight: 700;
}
.round-dot {
  display: inline-block;
  width: 20px;
  height: 20px;
  line-height: 20px;
  margin-right: 3px;
  border-radius: 4px;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
}
.pipe-table >>> td {
  vertical-align: top;
  padding-top: 6px !important;
  padding-bottom: 6px !important;
}
</style>
